import { createStore, filterNil, select, withProps } from '@ngneat/elf';
import {
  getAllEntities,
  selectAllEntities,
  setEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { joinRequestResult } from '@ngneat/elf-requests';
import { trackRequest } from '@tmx/core/elf-utils';
import {
  CostProfile,
  DefaultRoutingProfile,
  PagedCollectionParams,
  RoutingProfile,
  SimpleVehicleGroup,
  VehicleDimensionsThreshold,
} from '@tmx/data-access';
import {
  LoadingRowsDescriptor,
  RowDescriptor,
  SelectAllState,
  UpdateRowsDescriptor,
} from '@tmx/operandi';
import { combineLatest, map, Subject } from 'rxjs';

import { AddEditModalState } from '../../../../../models/add-edit-modal-state';
import { EMPTY_ROWS_DESCRIPTOR } from '../../../../../models/empty-rows-descriptor';
import { dataTablePageSizeSnapshot } from '../../../../../repositories/app-settings/app-settings.repository';
import { requestCompletedAndSucceeded } from '../../../../../utils/request-succeeded';

type RequestKeys =
  | 'create-routing-profile'
  | 'delete-routing-profile'
  | 'duplicate-routing-profile'
  | 'fetch-cost-profiles'
  | 'fetch-routing-profile-defaults'
  | 'fetch-routing-profile-details'
  | 'fetch-routing-profile-vehicle-groups'
  | 'fetch-routing-profiles'
  | 'routing-profile-vehicle-groups-assign'
  | 'routing-profile-vehicle-groups-unassign'
  | 'update-routing-profile';

interface RoutingProfilesProps {
  addEditRoutingProfileModalState: AddEditModalState;
  availableCostProfiles: CostProfile[];
  defaultProfileTemplates: DefaultRoutingProfile[];
  hasRoutingProfiles: boolean;
  routingProfileErrorMessage: null | string;
  routingProfilesSize: number;
  routingProfileTableParams: PagedCollectionParams;
  selectedRoutingProfile: null | RoutingProfile;
  selectedRoutingProfileVehicleGroups: SimpleVehicleGroup[];
  vehicleDimensionsThresholds: VehicleDimensionsThreshold[];
}

const routingProfilesStore = createStore(
  { name: 'routing-profiles' },
  withProps<RoutingProfilesProps>({
    defaultProfileTemplates: [],
    vehicleDimensionsThresholds: [],
    routingProfileTableParams: new PagedCollectionParams({
      offset: 0,
      limit: dataTablePageSizeSnapshot(),
      sortDirection: 'asc',
      sortExpression: 'name',
    }),
    routingProfilesSize: 0,
    hasRoutingProfiles: false,
    selectedRoutingProfile: null,
    selectedRoutingProfileVehicleGroups: [],
    routingProfileErrorMessage: null,
    addEditRoutingProfileModalState: {
      isOpen: false,
    },
    availableCostProfiles: [],
  }),
  withEntities<RoutingProfile, 'routingProfileId'>({
    idKey: 'routingProfileId',
  })
);

/** Snapshots */

export const routingProfilesStoreSnapshot = () =>
  routingProfilesStore.getValue();

export const routingProfilesSnapshot = () =>
  routingProfilesStore.query(getAllEntities());

export const selectedRoutingProfileSnapshot = () =>
  routingProfilesStore.getValue().selectedRoutingProfile;

/** Request Results Trackers */

export const trackRoutingProfilesRequests = <T>(keys: RequestKeys[]) =>
  trackRequest<T, RequestKeys>(keys);

export const joinRoutingProfilesRequests = <T>(keys: RequestKeys[]) =>
  joinRequestResult<T>(keys);

/** Exposed Variables and Observables */

export const currentStepReset$ = new Subject<number>();
export const currentStepSave$ = new Subject<number>();

export const routingProfilesRequestStatus$ = routingProfilesStore.pipe(
  selectAllEntities(),
  joinRoutingProfilesRequests(['fetch-routing-profiles'])
);

export const routingProfileDefaultsRequestStatus$ = routingProfilesStore.pipe(
  select(({ defaultProfileTemplates, vehicleDimensionsThresholds }) => ({
    defaultProfileTemplates,
    vehicleDimensionsThresholds,
  })),
  joinRoutingProfilesRequests(['fetch-routing-profile-defaults'])
);

export const routingProfilePageInitialized$ = combineLatest([
  routingProfilesRequestStatus$.pipe(requestCompletedAndSucceeded()),
  routingProfileDefaultsRequestStatus$.pipe(requestCompletedAndSucceeded()),
]).pipe(map((results) => results.every((completed) => completed)));

export const createRoutingProfileRequestStatus$ = routingProfilesStore.pipe(
  joinRoutingProfilesRequests(['create-routing-profile'])
);

export const duplicateRoutingProfileRequestStatus$ = routingProfilesStore.pipe(
  joinRoutingProfilesRequests(['duplicate-routing-profile'])
);

export const deleteRoutingProfileRequestStatus$ = routingProfilesStore.pipe(
  joinRoutingProfilesRequests(['delete-routing-profile'])
);

export const selectedRoutingProfileDetailsRequestStatus$ =
  routingProfilesStore.pipe(
    select(({ selectedRoutingProfile }) => selectedRoutingProfile),
    joinRoutingProfilesRequests(['fetch-routing-profile-details'])
  );

export const selectedRoutingProfileVehicleGroupsRequestStatus$ =
  routingProfilesStore.pipe(
    select(
      ({ selectedRoutingProfileVehicleGroups }) =>
        selectedRoutingProfileVehicleGroups
    ),
    joinRoutingProfilesRequests(['fetch-routing-profile-vehicle-groups'])
  );

export const routingProfileVehicleGroupsUnassignRequestStatus$ =
  routingProfilesStore.pipe(
    select(
      ({ selectedRoutingProfileVehicleGroups }) =>
        selectedRoutingProfileVehicleGroups
    ),
    joinRoutingProfilesRequests(['routing-profile-vehicle-groups-unassign'])
  );

export const routingProfileVehicleGroupsAssignRequestStatus$ =
  routingProfilesStore.pipe(
    select(
      ({ selectedRoutingProfileVehicleGroups }) =>
        selectedRoutingProfileVehicleGroups
    ),
    joinRoutingProfilesRequests(['routing-profile-vehicle-groups-assign'])
  );

export const updateRoutingProfileRequestStatus$ = routingProfilesStore.pipe(
  select(({ selectedRoutingProfile }) => selectedRoutingProfile),
  joinRoutingProfilesRequests(['update-routing-profile'])
);

export const costProfilesRequestStatus$ = routingProfilesStore.pipe(
  joinRequestResult(['fetch-cost-profiles'])
);

export const routingProfilesRowsDescriptor$ = routingProfilesStore.pipe(
  selectAllEntities(),
  joinRoutingProfilesRequests(['fetch-routing-profiles']),
  map(({ isLoading, data }) => {
    if (isLoading) {
      return new LoadingRowsDescriptor();
    }

    if (data.length === 0) {
      return EMPTY_ROWS_DESCRIPTOR;
    }

    const { routingProfilesSize, routingProfileTableParams } =
      routingProfilesStoreSnapshot();

    const rows = data.map(
      (routingProfile) =>
        new RowDescriptor(routingProfile, (row) => row.data.name)
    );

    return new UpdateRowsDescriptor(
      rows,
      routingProfileTableParams.offset,
      routingProfilesSize,
      0,
      SelectAllState.Deselected
    );
  })
);

export const routingProfileErrorMessage$ = routingProfilesStore.pipe(
  select(({ routingProfileErrorMessage }) => routingProfileErrorMessage),
  filterNil()
);

export const addEditRoutingProfileModalState$ = routingProfilesStore.pipe(
  select(
    ({ addEditRoutingProfileModalState }) => addEditRoutingProfileModalState
  )
);

export const selectedRoutingProfileName$ = routingProfilesStore.pipe(
  select(({ selectedRoutingProfile }) => selectedRoutingProfile?.name ?? null)
);

export const availableCostProfiles$ = routingProfilesStore.pipe(
  select(({ availableCostProfiles }) => availableCostProfiles)
);

export const hasRoutingProfiles$ = routingProfilesStore.pipe(
  select(({ hasRoutingProfiles }) => hasRoutingProfiles)
);

/** Updater Functions */

export function updateCurrentStepReset(currentStep: number) {
  currentStepReset$.next(currentStep);
}

export function updateCurrentStepSave(currentStep: number) {
  currentStepSave$.next(currentStep);
}

export function updateRoutingProfiles(
  routingProfiles: RoutingProfile[],
  size: number
) {
  routingProfilesStore.update(
    (state) => ({
      ...state,
      routingProfilesSize: size,
    }),
    setEntities(routingProfiles)
  );
}

export function updateRoutingProfileDefaults(
  defaultProfileTemplates: DefaultRoutingProfile[],
  vehicleDimensionsThresholds: VehicleDimensionsThreshold[]
) {
  routingProfilesStore.update((state) => ({
    ...state,
    defaultProfileTemplates,
    vehicleDimensionsThresholds,
  }));
}

export function updateRoutingProfileTableParams(
  routingProfileTableParams: PagedCollectionParams
) {
  routingProfilesStore.update((state) => ({
    ...state,
    routingProfileTableParams,
  }));
}

export function updateHasRoutingProfiles(hasRoutingProfiles: boolean) {
  routingProfilesStore.update((state) => ({
    ...state,
    hasRoutingProfiles,
  }));
}

export function updateSelectedRoutingProfile(
  selectedRoutingProfile: null | RoutingProfile
) {
  routingProfilesStore.update((state) => ({
    ...state,
    selectedRoutingProfile,
  }));
}

export function updateSelectedRoutingProfileVehicleGroups(
  selectedRoutingProfileVehicleGroups: SimpleVehicleGroup[]
) {
  routingProfilesStore.update((state) => ({
    ...state,
    selectedRoutingProfileVehicleGroups,
  }));
}

export function updateRoutingProfileErrorMessage(
  routingProfileErrorMessage: null | string
) {
  routingProfilesStore.update((state) => ({
    ...state,
    routingProfileErrorMessage,
  }));
}

export function updateAddEditRoutingProfileModalState(
  addEditRoutingProfileModalState: AddEditModalState
) {
  routingProfilesStore.update((state) => ({
    ...state,
    addEditRoutingProfileModalState,
  }));
}

export function updateAvailableCostProfiles(
  availableCostProfiles: CostProfile[]
) {
  routingProfilesStore.update((state) => ({
    ...state,
    availableCostProfiles,
  }));
}

export function resetRoutingProfilesStore() {
  routingProfilesStore.reset();
}
